import { getAcademicSessions } from 'app/crud/academicSessions.crud';
import { put, select, takeLatest } from 'redux-saga/effects';
import { REDUCER_ACADEMIC_SESSIONS } from '../conf';

export const actionTypes = {
  GetAcademicSessions: 'academicSessions/GET_ACADEMIC_SESSIONS',
  SetAcademicSessions: 'academicSessions/SET_ACADEMIC_SESSIONS',
  SetAcademicSessionsError: 'academicSessions/SET_ACADEMIC_SESSIONS_ERROR',
  ClearCache: 'academicSessions/CLEAR_CACHE',
};

const initialState = {
  loading: false,
  data: [],
  error: null,
  lastFetch: null,
  cacheExpiry: 5 * 60 * 1000,
};

export function reducer(state = initialState, action) {
  switch (action.type) {
    case actionTypes.GetAcademicSessions:
      return { ...state, loading: true, error: null };

    case actionTypes.SetAcademicSessions:
      return {
        ...state,
        loading: false,
        data: action.payload,
        error: null,
        lastFetch: Date.now(),
      };

    case actionTypes.SetAcademicSessionsError:
      return { ...state, loading: false, error: action.payload };

    case actionTypes.ClearCache:
      return { ...state, data: [], lastFetch: null, error: null };

    default:
      return state;
  }
}

function getBranch(state) {
  return state.entities?.[REDUCER_ACADEMIC_SESSIONS] ?? initialState;
}

export const selectors = {
  getLoading: (state) => getBranch(state).loading,
  getData: (state) => getBranch(state).data,
  getAcademicSessions: (state) => filterCurrentAndFuture(getBranch(state).data),
  getError: (state) => getBranch(state).error,
  getLastFetch: (state) => getBranch(state).lastFetch,
  isCacheValid: (state) => {
    const { data, lastFetch, cacheExpiry } = getBranch(state);
    if (!lastFetch || !data.length) return false;
    return Date.now() - lastFetch < cacheExpiry;
  },
};

export const actions = {
  getAcademicSessions: (forceRefresh = false) => ({
    type: actionTypes.GetAcademicSessions,
    payload: { forceRefresh },
  }),
  setAcademicSessions: (data) => ({ type: actionTypes.SetAcademicSessions, payload: data }),
  setAcademicSessionsError: (error) => ({ type: actionTypes.SetAcademicSessionsError, payload: error }),
  clearCache: () => ({ type: actionTypes.ClearCache }),
};

export function* saga() {
  yield takeLatest(actionTypes.GetAcademicSessions, function* getAcademicSessionsSaga(action) {
    try {
      const { forceRefresh } = action.payload;

      if (!forceRefresh) {
        const isCacheValid = yield select(selectors.isCacheValid);
        if (isCacheValid) return;
      }

      const response = yield getAcademicSessions();
      if (response?.cancelled) return;
      if (response?.error || response?.status !== 200) {
        yield put(actions.setAcademicSessionsError(response?.error || 'Error loading academic sessions'));
        return;
      }
      yield put(actions.setAcademicSessions(response?.data?.data || []));
    } catch (error) {
      yield put(actions.setAcademicSessionsError(error.message || 'Error loading academic sessions'));
    }
  });
}

// "Presente" = active (señal del backend); "futuro" = start_date posterior a hoy.
// No filtramos por end_date para evitar ocultar el ciclo actual cuando los datos están desactualizados.
export function filterCurrentAndFuture(items) {
  if (!Array.isArray(items)) return [];

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const filtered = items.filter((item) => {
    if (item?.active) return true;
    const start = parseDateLocal(item?.start_date);
    return start ? start > todayStart : false;
  });

  return filtered.sort((a, b) => {
    if (a.active !== b.active) return a.active ? -1 : 1;
    return (a.name || '').localeCompare(b.name || '');
  });
}

// Parsea date-only ("YYYY-MM-DD") en zona local para evitar off-by-one en TZs negativas.
// Las cadenas con TZ explícito (ISO timestamps) se parsean nativamente.
function parseDateLocal(input) {
  if (!input) return null;
  if (typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    const [year, month, day] = input.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const date = new Date(input);
  return Number.isNaN(date.getTime()) ? null : date;
}
